// ============================================================
// LibraryListView - the "This session" view of the Character Library:
// toolbar, the last verb's note, the selection bar, tag filter, the list
// and the single-character Add to roster.
//
// Multi-select: click = single, Ctrl/Cmd+click = toggle,
// Shift+click = range. Batch delete, batch export.
// ============================================================
import type { ChangeEvent, MouseEvent, RefObject } from 'react';
import type { Character } from '../../engine/character/types';
import common from '../../styles/common.module.css';
import styles from '../../styles/CharacterLibrary.module.css';
import clsx from '../../utils/clsx';
import { applySelection } from '../../utils/listSelection';
import { expandByCount } from '../../utils/expandByCount';
import LibraryRow from './LibraryRow';
import { libraryLists, roomName } from './libraryModel';
import type { CharacterLibraryProps } from './libraryModel';
import type { LibraryPanel } from './useLibraryPanel';

type Lists = ReturnType<typeof libraryLists>;

interface ViewProps {
  props: CharacterLibraryProps;
  panel: LibraryPanel;
  fileRef: RefObject<HTMLInputElement | null>;
}

interface PartProps extends ViewProps {
  lists: Lists;
}

/** Download the selection - or everyone, when nothing is picked - as one JSON file. */
function exportSelection({ library }: CharacterLibraryProps, selectedIds: Set<string>) {
  const ids = selectedIds.size > 0 ? [...selectedIds] : library.characters.map((c) => c.id);
  const json = library.exportCharacters(ids);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `characters-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Read a chosen JSON file into the library; a bad file is logged, never thrown into the panel. */
export function importFromInput(e: ChangeEvent<HTMLInputElement>, importCharacters: (json: string) => unknown) {
  const file = e.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      importCharacters(reader.result as string);
    } catch (err) {
      console.error('Character import failed:', err);
    }
  };
  reader.readAsText(file);
  e.target.value = '';
}

/** Delete the selection - asking first when it is more than one. */
function DeleteButton({ props, panel }: ViewProps) {
  const { selectedIds, setSelectedIds } = panel.selection;
  const remove = () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size > 1 && !window.confirm(`Delete ${selectedIds.size} characters?`)) return;
    selectedIds.forEach((id) => props.library.deleteCharacter(id));
    setSelectedIds(new Set());
  };
  return (
    <button
      className={common.btnSmall}
      style={{ color: selectedIds.size > 0 ? 'var(--danger)' : undefined }}
      onClick={remove}
      disabled={selectedIds.size === 0}
    >
      Delete{selectedIds.size > 1 ? ` (${selectedIds.size})` : ''}
    </button>
  );
}

/** New, edit, duplicate and delete - the single-list verbs. */
function EditButtons(part: PartProps) {
  const { props, panel } = part;
  const single = part.lists.single;
  const duplicate = () => {
    if (!single) return;
    const clone = props.library.duplicateCharacter(single.id);
    if (clone) panel.selection.setSelectedIds(new Set([clone.id]));
  };
  return (
    <>
      <button className={common.btnSmall} onClick={props.onNew}>
        + New
      </button>
      <button className={common.btnSmall} onClick={() => single && props.onEdit(single)} disabled={!single}>
        Edit
      </button>
      <button className={common.btnSmall} onClick={duplicate} disabled={!single} title="Duplicate character">
        Duplicate
      </button>
      <DeleteButton {...part} />
    </>
  );
}

/**
 * THE TWO VERBS THAT WERE MISSING. This list could import, export and
 * delete in bulk, and then made you load them one at a time through a
 * spawn dialog - so bulk import stopped one step short of the thing it
 * was for (v8.23.1).
 */
function LoadButton({ props, panel, lists }: PartProps) {
  const { onLoadMany, onLoadCharacter, onClose } = props;
  const { picked, total } = lists;
  if (!onLoadMany) return null;
  const load = () => {
    // ONE OF ONE STILL GETS THE DIALOG. It is the only way to say "act
    // as this one" or "target this one", and those are decisions about
    // a combatant you are about to play rather than about a squad you
    // are placing.
    if (picked.length === 1 && total === 1 && onLoadCharacter) {
      onLoadCharacter(picked[0]);
      onClose();
      return;
    }
    onLoadMany(expandByCount(picked, panel.counts));
    panel.setNote(`Loaded ${total} onto the roster.`);
  };
  return (
    <button
      className={common.btnSmall}
      data-testid="library-load-selected"
      disabled={panel.selection.selectedIds.size === 0}
      title="Put them straight onto the roster and the map"
      onClick={load}
    >
      Load{total > 1 ? ` (${total})` : ''}
    </button>
  );
}

/**
 * Stage the picked, counts and all. THE COUNT COUNTS HERE TOO: it read
 * the picked rows straight through, so a row set to three staged one
 * person while the button beside it said (3) - the count input was
 * Load's alone by accident (v8.34.0).
 */
function StageButton({ props, panel, lists }: PartProps) {
  const { onSendToRoom, waitingRooms = [] } = props;
  if (!onSendToRoom) return null;
  const stage = () => {
    const where = onSendToRoom(expandByCount(lists.picked, panel.counts), panel.stageRoomId || undefined);
    // A ROOM NAMED TRULY. The handler knows which room it actually used
    // - it may have just made one - and returns null when nothing
    // happened, so a cancelled prompt says nothing rather than lying.
    if (where === null) return;
    const room = typeof where === 'string' ? where : roomName(waitingRooms, panel.stageRoomId);
    panel.setNote(`Staged ${lists.total} → ${room}.`);
  };
  return (
    <button
      className={common.btnSmall}
      data-testid="library-stage-selected"
      disabled={panel.selection.selectedIds.size === 0}
      title="Stage them where nobody else can see them"
      onClick={stage}
    >
      → Waiting{lists.total > 1 ? ` (${lists.total})` : ''}
    </button>
  );
}

/**
 * ALWAYS THERE, because it can make a room. It appeared only with two
 * rooms to choose between, so staging from a session with none did
 * nothing at all and the button's own title sent you to Deploy mode to
 * make one by hand - the dead end the Folder tab shed in v8.26.0, still
 * standing here (v8.34.0).
 */
function RoomSelect({ props, panel }: ViewProps) {
  if (!props.onSendToRoom) return null;
  return (
    <select
      className={common.input}
      aria-label="Waiting room"
      data-testid="library-room"
      value={panel.stageRoomId}
      onChange={(e) => panel.setStageRoomId(e.target.value)}
    >
      <option value="">Active room</option>
      {(props.waitingRooms ?? []).map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
      <option value="new:">＋ New room…</option>
    </select>
  );
}

function Toolbar(part: PartProps) {
  const { selectedIds } = part.panel.selection;
  return (
    <div className={styles.toolbar}>
      <EditButtons {...part} />
      <LoadButton {...part} />
      <StageButton {...part} />
      <RoomSelect {...part} />
      <span style={{ flex: 1 }} />
      <button
        className={common.btnSmall}
        onClick={() => exportSelection(part.props, selectedIds)}
        title="Export selected or all"
      >
        Export{selectedIds.size > 1 ? ` (${selectedIds.size})` : ''}
      </button>
      <button className={common.btnSmall} onClick={() => part.fileRef.current?.click()}>
        Import
      </button>
    </div>
  );
}

/** "N selected", Select All, Deselect - while there is anyone to select. */
function SelectionBar({ props, panel }: ViewProps) {
  const { characters } = props.library;
  const { selectedIds, setSelectedIds } = panel.selection;
  if (characters.length === 0) return null;
  return (
    <div className={styles.selectionBar}>
      {selectedIds.size > 0 && <span className={styles.selectionCount}>{selectedIds.size} selected</span>}
      <span style={{ flex: 1 }} />
      {selectedIds.size < characters.length && (
        <button className={styles.selectionBtn} onClick={() => setSelectedIds(new Set(characters.map((c) => c.id)))}>
          Select All
        </button>
      )}
      {selectedIds.size > 0 && (
        <button className={styles.selectionBtn} onClick={() => setSelectedIds(new Set())}>
          Deselect
        </button>
      )}
    </div>
  );
}

/** All, then one button per tag; pressing the active tag again clears it. */
function TagFilter({ props }: ViewProps) {
  const { allTags, activeTag, filterByTag } = props.library;
  if (allTags.length === 0) return null;
  return (
    <div className={styles.tagFilter}>
      <button className={clsx(styles.tagBtn, !activeTag && styles.tagBtnActive)} onClick={() => filterByTag(null)}>
        All
      </button>
      {allTags.map((tag) => (
        <button
          key={tag}
          className={clsx(styles.tagBtn, activeTag === tag && styles.tagBtnActive)}
          onClick={() => filterByTag(activeTag === tag ? null : tag)}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

/**
 * The rows, recent first. CLICK THE BLANK SPACE TO DROP THE SELECTION -
 * the gesture every file manager has. The rows stop the click, so only
 * the space below them clears (v8.26.0).
 */
function CharacterList({ props, panel, lists }: PartProps) {
  const { characters, loading } = props.library;
  const { selectedIds, setSelectedIds, lastClickedId } = panel.selection;
  /**
   * ONE GRAMMAR, shared with the Folder tab: click adds, right-click
   * removes, shift+click takes a range. It used to replace on a plain
   * click here and in the folder view, so the same menu taught two
   * different meanings for the same button (v8.24.0).
   */
  const pick = (c: Character, e: MouseEvent, remove = false) => {
    const current = { order: lists.flat.map((x) => x.id), selected: selectedIds, anchor: lastClickedId.current };
    const out = applySelection(current, c.id, { shift: e.shiftKey, remove });
    setSelectedIds(out.selected);
    lastClickedId.current = out.anchor;
  };
  const countable = !!(props.onLoadMany || props.onSendToRoom);
  const row = (c: Character) => (
    <LibraryRow key={c.id} character={c} panel={panel} onPick={pick} onEdit={props.onEdit} countable={countable} />
  );
  const clearOnBlank = (e: MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    setSelectedIds(new Set());
    lastClickedId.current = null;
  };
  return (
    // Mouse-only by the user's choice (2026-09-27): a blank-space click clears the selection.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.charList} onClick={clearOnBlank}>
      {loading && <div className={styles.empty}>Loading character library…</div>}
      {!loading && characters.length === 0 && (
        <div className={styles.empty}>No characters yet. Click &quot;+ New&quot; to create one.</div>
      )}
      {lists.recent.length > 0 && characters.length > 0 && (
        <div className={styles.recentSection} data-testid="library-recent">
          <div className={styles.recentLabel}>Recently Loaded</div>
          {lists.recent.map(row)}
        </div>
      )}
      {lists.others.map(row)}
    </div>
  );
}

/**
 * Load action - only for single selection.
 * v7.4.0: ONE action. Under the unified roster there is no "as
 * attacker" vs "as target" - there is one combatant, and the dialog
 * that follows decides which of your private pointers move. The old
 * "Load as Both" ran both paths and duplicated the person.
 */
function AddToRoster({ props, lists }: PartProps) {
  const { single } = lists;
  const { onLoadCharacter, onClose } = props;
  if (!single || !onLoadCharacter) return null;
  return (
    <div className={styles.actions}>
      <button
        className={common.btnSmall}
        onClick={() => {
          onLoadCharacter(single);
          onClose();
        }}
      >
        Add to roster
      </button>
    </div>
  );
}

export default function LibraryListView({ props, panel, fileRef }: ViewProps) {
  const { characters, recentIds } = props.library;
  const lists = libraryLists(characters, recentIds, panel.selection.selectedIds, panel.counts);
  const part = { props, panel, fileRef, lists };
  return (
    <>
      <Toolbar {...part} />
      {/* WHAT IT JUST DID. Load and → Waiting both act and then look
          exactly as they did a moment before: the menu stays open, the
          selection stays picked, and staging in particular sends people
          somewhere invisible from here. The Folder tab has said so since
          v8.23.1; this one did not (v8.38.0).

          NEVER A CHARACTER'S NAME - counts and rooms only. Specs and
          helpers click characters by bare text, and a note carrying a
          name makes those locators ambiguous. */}
      {panel.note && (
        <p className={styles.notice} data-testid="library-action-note" role="status" aria-live="polite">
          {panel.note}
        </p>
      )}
      <SelectionBar {...part} />
      <TagFilter {...part} />
      <CharacterList {...part} />
      <AddToRoster {...part} />
    </>
  );
}
